/**
 * Cross-tab notification coordination using BroadcastChannel.
 *
 * Handles two concerns:
 * 1. Browser notification dedup — only one tab shows the OS notification for a given ID
 * 2. Dismiss sync — dismissing in one tab propagates to all other tabs
 *
 * BroadcastChannel messages are NOT received by the sending tab, so there are no loops.
 */
import { useNotificationStore } from "src/stores/notificationStore";

const CHANNEL_NAME = "fraction-notifications";

const claimedIds = new Set<string>();
let channel: BroadcastChannel | null = null;

function ensureChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === "undefined") return null;
  if (!channel) {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (e: MessageEvent) => {
      const { type, id } = e.data;
      switch (type) {
        case "claimed":
          claimedIds.add(id);
          break;
        case "dismiss":
          useNotificationStore.getState().dismissNotification(id);
          break;
        case "dismiss-many":
          useNotificationStore.getState().dismissMany(e.data.ids);
          break;
        case "dismiss-all":
          useNotificationStore.getState().dismissAll();
          break;
      }
    };
  }
  return channel;
}

/**
 * Attempt to claim a browser notification. Returns true if this tab should show it.
 * If another tab already claimed it (via an earlier BroadcastChannel message), returns false.
 * The Notification API's `tag` property acts as a safety net for near-simultaneous claims.
 */
export function claimBrowserNotification(id: string): boolean {
  ensureChannel();
  if (claimedIds.has(id)) return false;
  claimedIds.add(id);
  channel?.postMessage({ type: "claimed", id });
  return true;
}

/** Tell other tabs to dismiss a notification. */
export function broadcastDismiss(id: string): void {
  ensureChannel()?.postMessage({ type: "dismiss", id });
}

/** Tell other tabs to dismiss multiple notifications. */
export function broadcastDismissMany(ids: string[]): void {
  if (ids.length === 0) return;
  ensureChannel()?.postMessage({ type: "dismiss-many", ids });
}

/** Tell other tabs to dismiss all notifications. */
export function broadcastDismissAll(): void {
  ensureChannel()?.postMessage({ type: "dismiss-all" });
}

// Initialize channel eagerly so we're listening before any notifications arrive
ensureChannel();
